import { API_URL } from '../../config';
import { getSkillById } from '../gphSkillRegistry';
import {
  validateSensiIaSlots,
  buildMissingSensiIaSlotsMessage,
  buildInvalidSensiIaSlotsMessage
} from '../validation/sensiIaSlotValidator';
import {
  renderSensiIaResult,
  renderSensiIaHumanError
} from '../renderers/sensiIaResultRenderer';

const SENSI_DIAGNOSTIC_SCHEMA_VERSION = 'sensi_diagnostic_v1';

const readResponseJson = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return {};
  }
};

const extractBackendMessage = (data = {}) => {
  const detail = data.detail || data.message || data.error;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail.message === 'string') return detail.message;
  return null;
};

export const buildSensiIaReportOnlyPayload = (slots, { language = 'pt-BR' } = {}) => ({
  schema_version: SENSI_DIAGNOSTIC_SCHEMA_VERSION,
  mode: 'report_only',
  language,
  locale: language,
  game_id: slots.game,
  context: {
    weapon: slots.weapon || null,
    scope: slots.scope || null,
    control_mode: slots.gyroMode || null,
    gyro_mode: slots.gyroMode || null,
    distance_range: slots.distance || null,
    fps: slots.fps || null,
    fingers: slots.fingers || null,
    device_type: slots.device || 'phone'
  },
  current_settings: slots.currentSettings || {},
  symptom_report: slots.symptomReport,
  video: {
    provided: false,
    metrics: null
  },
  history: {
    previous_session_id: null,
    last_recommendation_applied: null,
    user_feedback: null
  }
});

export const prepareSensiIaSkill = ({
  slots = {},
  mode = 'report_only',
  language = 'pt-BR'
} = {}) => {
  const skill = getSkillById('sensi_ia');
  const validation = validateSensiIaSlots(slots, mode);

  if (validation.invalidSlots.length > 0) {
    return {
      status: 'invalid',
      skill,
      slots: validation.normalizedSlots,
      missingSlots: validation.missingSlots,
      invalidSlots: validation.invalidSlots,
      message: buildInvalidSensiIaSlotsMessage(validation.invalidSlots)
    };
  }

  if (validation.missingSlots.length > 0) {
    return {
      status: 'needs_slots',
      skill,
      slots: validation.normalizedSlots,
      missingSlots: validation.missingSlots,
      invalidSlots: [],
      message: buildMissingSensiIaSlotsMessage(validation.missingSlots)
    };
  }

  return {
    status: 'ready',
    skill,
    slots: validation.normalizedSlots,
    missingSlots: [],
    invalidSlots: [],
    payload: buildSensiIaReportOnlyPayload(validation.normalizedSlots, { language }),
    message: null
  };
};

export const executeSensiIaSkill = async ({
  slots = {},
  mode = 'report_only',
  fetchImpl = globalThis.fetch,
  apiBaseUrl = API_URL,
  authToken = null,
  getAuthToken = null,
  language = 'pt-BR'
} = {}) => {
  const prepared = prepareSensiIaSkill({ slots, mode, language });

  if (prepared.status !== 'ready') {
    return prepared;
  }

  if (typeof fetchImpl !== 'function') {
    return {
      ...prepared,
      status: 'error',
      message: 'Nao consegui conectar a Sensi-IA agora. Tenta novamente em instantes.'
    };
  }

  const payload = buildSensiIaReportOnlyPayload(prepared.slots, { language });

  let response;
  try {
    const token = authToken || (typeof getAuthToken === 'function' ? await getAuthToken() : null);
    response = await fetchImpl(`${apiBaseUrl}/sensi/diagnose`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(payload)
    });
  } catch (error) {
    return {
      ...prepared,
      status: 'error',
      payload,
      message: 'Nao consegui conectar a Sensi-IA agora. Tenta novamente em instantes.'
    };
  }

  const data = await readResponseJson(response);

  if (!response.ok) {
    return {
      ...prepared,
      status: 'blocked_or_error',
      payload,
      backendStatus: response.status,
      message: renderSensiIaHumanError({
        status: response.status,
        message: extractBackendMessage(data)
      })
    };
  }

  return {
    ...prepared,
    status: 'success',
    payload,
    result: renderSensiIaResult(data)
  };
};

export default executeSensiIaSkill;
